import { useBankProfile } from '@contexts/BankProfileContext';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import type { AccountStatus } from 'types/operations';

import { useAuth } from '../../hooks';
import type { PaginatedItems } from './common';

export interface Account {
	id: number;
	uuid: string;
	iban: string;
	currency: string;
	ownerName: string;
	productId: number;
	type: string;
	status: AccountStatus;
	bic: string;
	externalId: never;
	createdAt: string;
	lastUsedAt: never;
	lastChangedAt: string;
	externalData: never[];
	externalReferences: never[];
	balances: Balance[];
	balance: number;
	availableBalance: number;
	reservedAmount: number;
	label: string | null;
}

export interface Balance {
	amount: number;
	currency: string;
	display: string;
	balanceType: 'SettlementPositionBalance' | 'AvailableBalance';
	referenceDate: never;
	lastChangeDateTime: never;
}

export const ACCOUNTS_PAGE_SIZE = 20;

interface AccountListParams {
	currentPage?: number;
	limit?: number;
	iban?: string;
	name?: string;
	currency?: string;
	status?: AccountStatus[];
}

export const useAccounts = ({
	currentPage = 1,
	limit = ACCOUNTS_PAGE_SIZE,
	iban,
	name,
	currency,
	status = [],
}: AccountListParams) => {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();

	const offset = currentPage === 1 ? 0 : (currentPage - 1) * limit;

	const fetchAccounts = async (): Promise<PaginatedItems<Account[]>> => {
		const response = await axios.get(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${selectedProfile?.id}/accounts`,
			{
				params: {
					offset,
					limit,
					...(iban && { iban }),
					...(name && { name }),
					...(currency && { currency: currency.toUpperCase() }),
					...(status.length > 0 && {
						status: status?.map((s) => s.toUpperCase()).join(','),
					}),
				},
				headers: { Authorization: session?.tokenized },
			},
		);

		return response.data;
	};

	return useQuery({
		queryKey: [
			'accounts',
			selectedProfile?.id,
			currentPage,
			limit,
			iban,
			name,
			currency,
			status,
		],
		queryFn: () => fetchAccounts(),
		enabled: !!selectedProfile,
		select: (data) => {
			const items = data?.items?.map((acc) => ({
				...acc,
				availableBalance: acc.availableBalance / 100,
				reservedAmount: acc.reservedAmount / 100,
				balance: acc.balance / 100,
			}));

			return {
				...data,
				items,
			};
		},
	});
};

export const useAccountDetails = (accountUuid?: string) => {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();

	const fetchAccount = async (): Promise<Account> => {
		const response = await axios.get(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${selectedProfile?.id}/accounts/${accountUuid}`,
			{
				headers: { Authorization: session?.tokenized },
			},
		);

		return response.data;
	};

	return useQuery({
		queryKey: ['accounts', selectedProfile?.id, accountUuid],
		queryFn: fetchAccount,
		enabled: !!accountUuid && !!selectedProfile,
		select: (acc) => ({
			...acc,
			availableBalance: acc.availableBalance / 100,
			reservedAmount: acc.reservedAmount / 100,
			balance: acc.balance / 100,
		}),
	});
};
